import * as React from 'react';

import {Nut_Settings_User} from 'nutshell-core/settings-constants';
import {
    PROTOCOL_OPTIONS,
    getNormalizedDomain,
} from 'nutshell-core/utils/domain/get-normalized-domain';
import type {ProtocolOption} from 'nutshell-core/utils/domain/get-normalized-domain';

import {Button} from 'shells/button/button';
import {PageTitleBar} from 'shells/layout/page-title-bar';
import {Stack} from 'shells/layout/stack/stack';
import {FooterBar} from 'shells/modal/footer-bar';

import {useUpdateSetting} from '../../../core/use-update-setting';

import {analyticsClient} from '../../../../analytics-client';
import {appHistory} from '../../../../history';

import {useGetChatSettings} from '../graphql/hooks/use-get-chat-settings';
import {useGetContextDocuments} from '../graphql/hooks/use-get-context-documents';
import {useTriggerPagesCrawl} from '../graphql/hooks/use-trigger-pages-crawl';

import {ChatOnboardingBreadcrumbs} from './chat-onboarding-breadcrumbs';
import {VIEWS, SUB_PAGE_LIMIT} from './consts';
import type {ChatOnboardingView} from './consts';
import {getNextView} from './helpers';
import {EmbedChatbot} from './views/embed-chatbot';
import {InteractivePreview} from './views/interactive-preview';
import {YourWebsite} from './views/your-website';

export function ChatOnboarding() {
    const [currentView, setCurrentView] = React.useState<ChatOnboardingView>(
        VIEWS.YOUR_WEBSITE_VIEW
    );
    const [domain, setDomain] = React.useState<string>('');
    // @ts-expect-error
    const [protocol, setProtocol] = React.useState<ProtocolOption>(PROTOCOL_OPTIONS[0]);
    const [errorMessage, setErrorMessage] = React.useState<string>('');
    const [primaryDocumentPending, setPrimaryDocumentPending] = React.useState<boolean>(false);
    const [primaryDocumentErrored, setPrimaryDocumentErrored] = React.useState<boolean>(false);

    const {triggerPagesCrawl, error: errorCrawling} = useTriggerPagesCrawl();
    const {refetch: refetchSettings} = useGetChatSettings();
    const {primaryContextDocument, refetch} = useGetContextDocuments(
        true,
        undefined,
        refetchSettings
    );
    const updateSetting = useUpdateSetting();

    const fullUrl = getNormalizedDomain(protocol, domain);

    React.useEffect(() => {
        analyticsClient.track(analyticsClient.EVENTS.TRACK_CHATBOT_ONBOARDING_STARTED);
    }, []);

    React.useEffect(() => {
        if (domain) {
            return;
        }

        const signupDomain = NutClientConfig.signupWebsite;

        if (signupDomain) {
            setDomain(signupDomain);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        if (errorCrawling) {
            setErrorMessage(errorCrawling);
            setCurrentView(VIEWS.YOUR_WEBSITE_VIEW);
        }
    }, [errorCrawling]);

    React.useEffect(() => {
        if (primaryContextDocument && primaryContextDocument.effectiveStatus !== 'PENDING') {
            setPrimaryDocumentPending(false);
        }
        if (primaryContextDocument && primaryContextDocument.effectiveStatus === 'FAILED') {
            setPrimaryDocumentErrored(true);
        }
    }, [primaryContextDocument]);

    const startCrawling = () => {
        if (!domain.trim()) {
            setErrorMessage('Please enter a domain');

            return;
        }

        triggerPagesCrawl({
            urls: [fullUrl],
            subpageLimit: SUB_PAGE_LIMIT,
            isOnboarding: true,
        }).then(() => {
            refetch();
            setPrimaryDocumentPending(true);
            setPrimaryDocumentErrored(false);
            setErrorMessage('');
            setCurrentView(VIEWS.INTERACTIVE_PREVIEW_VIEW);
        });
    };

    let content;
    let hideNextButton = false;
    let showSkipButton = false;
    let showChangeWebsiteButton = false;
    switch (currentView) {
        case VIEWS.YOUR_WEBSITE_VIEW:
            content = (
                <YourWebsite
                    domain={domain}
                    setDomain={setDomain}
                    protocol={protocol}
                    setProtocol={setProtocol}
                    onSubmit={startCrawling}
                    errorMessage={errorMessage}
                    protocolOptions={PROTOCOL_OPTIONS}
                />
            );
            showSkipButton = true;
            break;
        case VIEWS.INTERACTIVE_PREVIEW_VIEW:
            showChangeWebsiteButton = true;
            if (primaryDocumentPending) {
                hideNextButton = true;
                showSkipButton = true;
            }

            content = (
                <InteractivePreview
                    previewWebsiteUrl={fullUrl}
                    isCrawling={primaryDocumentPending}
                    isCrawlingErrored={primaryDocumentErrored}
                    setCurrentView={setCurrentView}
                    setErrorMessage={setErrorMessage}
                    canEmbedInIframe={
                        primaryContextDocument && primaryContextDocument.canEmbedIframe
                    }
                />
            );
            break;
        case VIEWS.EMBED_CHATBOT_VIEW:
            content = <EmbedChatbot />;
            showChangeWebsiteButton = true;
            break;
        default:
            content = null;
    }

    return (
        <div className='flex flex-dir-col full-width full-height border-box pos-absolute background-white border-top-left-8'>
            {currentView !== VIEWS.INTERACTIVE_PREVIEW_VIEW && (
                <PageTitleBar
                    title='AI Chat'
                    subheaderPrimaryText='Let Nutshell’s AI chatbot manage interactions with your customers'
                />
            )}
            <div className='flex flex-dir-col flex-grow overflow-auto full-height background-offwhite border-top-left-8'>
                {content}
            </div>
            <div className='border-top px-16'>
                <FooterBar
                    left={
                        <ChatOnboardingBreadcrumbs
                            currentView={currentView}
                            setCurrentView={setCurrentView}
                            domain={domain}
                            isCrawling={primaryDocumentPending}
                        />
                    }
                    right={
                        <Stack spacing={16} isHorizontal={true}>
                            {showSkipButton && (
                                <Button
                                    variant='text-secondary'
                                    size='big'
                                    onClick={() => {
                                        updateSetting({
                                            settingName:
                                                Nut_Settings_User.SETTING_HAS_SKIPPED_CHAT_ONBOARDING,
                                            value: true,
                                        });
                                        appHistory.push('/inbox/settings/chat');
                                    }}
                                >
                                    Skip
                                </Button>
                            )}
                            {showChangeWebsiteButton && (
                                <Button
                                    variant='secondary'
                                    size='big'
                                    onClick={() => {
                                        setCurrentView(VIEWS.YOUR_WEBSITE_VIEW);
                                        setDomain('');
                                    }}
                                >
                                    Change website
                                </Button>
                            )}
                            {!hideNextButton && (
                                <Button
                                    variant={
                                        currentView === VIEWS.EMBED_CHATBOT_VIEW
                                            ? 'save'
                                            : 'primary'
                                    }
                                    disabled={!domain.trim()}
                                    size='big'
                                    onClick={() => {
                                        if (currentView === VIEWS.YOUR_WEBSITE_VIEW) {
                                            startCrawling();

                                            return;
                                        }

                                        const nextView = getNextView(currentView);
                                        if (nextView) {
                                            setCurrentView(nextView);
                                        } else {
                                            appHistory.push('/inbox/settings/chat');
                                        }
                                    }}
                                >
                                    {currentView === VIEWS.EMBED_CHATBOT_VIEW
                                        ? 'Continue configuration'
                                        : 'Continue'}
                                </Button>
                            )}
                        </Stack>
                    }
                />
            </div>
        </div>
    );
}
